/**
 * EpisodesTab — search + archive past episodes (investigation_memory rows).
 *
 * Filters: free-text q, service substring, thumb (+1 / -1 / any), page.
 * Table cells: when, services, symptoms, causal-chain compact summary,
 * confidence, thumb chip, Archive action.
 *
 * The "q" input is debounced lightly — the admin types, waits 350ms,
 * then we fire. Page is reset to 1 on any filter change.
 */

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ApiError, memory } from '@/helpers/ApiHelper';
import type { CausalChain, InvestigationMemoryOut, ThumbValue } from '@/types/memory';

const PAGE_SIZE = 25;

type ThumbFilter = 'any' | '1' | '-1';

export interface EpisodesTabProps {
  /** When false, the Archive action is hidden. Backend enforces the same gate. */
  canEdit: boolean;
}

export function EpisodesTab({ canEdit }: EpisodesTabProps): ReactElement {
  const [q, setQ] = useState<string>('');
  const [debouncedQ, setDebouncedQ] = useState<string>('');
  const [service, setService] = useState<string>('');
  const [thumb, setThumb] = useState<ThumbFilter>('any');
  const [page, setPage] = useState<number>(1);

  const [rows, setRows] = useState<InvestigationMemoryOut[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [archive, setArchive] = useState<InvestigationMemoryOut | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  // Debounce q
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, service, thumb]);

  const refresh = useCallback(async () => {
    try {
      const data = await memory.listEpisodes({
        q: debouncedQ || undefined,
        service: service || undefined,
        thumb: thumb === 'any' ? undefined : (Number(thumb) as ThumbValue),
        page,
      });
      setRows(data.results);
      setTotal(data.count);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Failed to load episodes');
    }
  }, [debouncedQ, service, thumb, page]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onConfirmArchive = useCallback(async () => {
    if (!archive) return;
    setBusy(true);
    try {
      await memory.archiveEpisode(archive.id);
      setArchive(null);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Archive failed');
    } finally {
      setBusy(false);
    }
  }, [archive, refresh]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
      >
        <TextField
          size="small"
          label="Search narrative / symptoms"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 280, flex: 1 }}
          inputProps={{ 'aria-label': 'Search episodes' }}
        />
        <TextField
          size="small"
          label="Service"
          value={service}
          onChange={(e) => setService(e.target.value)}
          placeholder="e.g. scope"
          sx={{ minWidth: 180 }}
        />
        <Select
          size="small"
          value={thumb}
          onChange={(e) => setThumb(e.target.value as ThumbFilter)}
          sx={{ minWidth: 140 }}
          aria-label="Filter by feedback thumb"
        >
          <MenuItem value="any">any feedback</MenuItem>
          <MenuItem value="1">thumb up</MenuItem>
          <MenuItem value="-1">thumb down</MenuItem>
        </Select>
        <Chip label={`${total} episodes`} size="small" variant="outlined" />
      </Stack>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {!error && rows == null && (
        <Typography variant="body2" color="text.secondary">
          Loading…
        </Typography>
      )}

      {!error && rows != null && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No matching episodes.
        </Typography>
      )}

      {!error && rows != null && rows.length > 0 && (
        <>
          <TableContainer>
            <Table size="small" aria-label="Episodes">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Symptoms</TableCell>
                  <TableCell>Causal chain</TableCell>
                  <TableCell align="right">Confidence</TableCell>
                  <TableCell>Thumb</TableCell>
                  {canEdit && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((ep) => (
                  <EpisodeRow
                    key={ep.id}
                    episode={ep}
                    canEdit={canEdit}
                    onArchive={(e) => setArchive(e)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, v) => setPage(v)}
              size="small"
            />
          </Box>
        </>
      )}

      {archive && (
        <Dialog open onClose={() => setArchive(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Archive episode</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                Archived episodes are excluded from semantic retrieval but kept
                in the table for audit. This action can be undone by clearing
                the row&apos;s <code>archived_at</code> directly in the DB.
              </Typography>
              <Typography variant="body2">
                {archive.services.join(', ') || '(no services)'} —
                {' '}
                {archive.symptoms.join(', ') || '(no symptoms)'}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchive(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={onConfirmArchive}
              disabled={busy}
            >
              Archive
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

interface EpisodeRowProps {
  episode: InvestigationMemoryOut;
  canEdit: boolean;
  onArchive: (ep: InvestigationMemoryOut) => void;
}

function EpisodeRow({ episode, canEdit, onArchive }: EpisodeRowProps): ReactElement {
  const when = episode.anchor_to ?? episode.created_at;
  const link =
    episode.source_type === 'investigation'
      ? `/investigations/${episode.source_id}`
      : `/issues`; // analyses live on their issue page; no direct deep link yet

  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Link
          component={RouterLink}
          to={link}
          underline="hover"
          variant="body2"
          sx={{ fontFamily: 'monospace' }}
        >
          {new Date(when).toLocaleString()}
        </Link>
      </TableCell>
      <TableCell sx={{ maxWidth: 180 }}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {episode.services.map((s) => (
            <Chip key={s} label={s} size="small" variant="outlined" />
          ))}
        </Stack>
      </TableCell>
      <TableCell sx={{ maxWidth: 200 }}>
        <Typography variant="caption" component="span">
          {episode.symptoms.join(', ')}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 320 }}>
        <CausalChainCell chain={episode.causal_chain} />
      </TableCell>
      <TableCell align="right">
        {episode.confidence != null ? episode.confidence.toFixed(2) : '—'}
      </TableCell>
      <TableCell>
        <ThumbCell thumb={episode.feedback_thumb} />
      </TableCell>
      {canEdit && (
        <TableCell align="right">
          {episode.archived_at ? (
            <Chip label="archived" size="small" variant="outlined" />
          ) : (
            <Tooltip title="Archive episode">
              <IconButton
                aria-label={`Archive episode ${episode.id}`}
                size="small"
                color="error"
                onClick={() => onArchive(episode)}
              >
                <ArchiveOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

/** Compact, line-clamped renderer for the Causal chain column.
 *
 * The underlying ``causal_chain`` JSONB can carry anywhere from a one-
 * phrase value per label (when parse_causal_chain works as designed) to
 * full multi-paragraph prose (when an earlier parser bug greedily slurped
 * the rest of the narrative). Either way we render compact: one label per
 * line, each clamped to a single visible line via WebkitLineClamp. The
 * full text is one click away via a dialog.
 *
 * Without this clamp, a single bloated row stretches the row height to
 * hundreds of pixels and pushes every other column into a sliver. */
function CausalChainCell({ chain }: { chain: CausalChain }): ReactElement {
  const [open, setOpen] = useState(false);
  const items: Array<{ label: string; value: string }> = [];
  if (chain.symptom) items.push({ label: 'sym', value: chain.symptom });
  if (chain.proximate_cause) items.push({ label: 'prox', value: chain.proximate_cause });
  if (chain.trigger) items.push({ label: 'trig', value: chain.trigger });
  if (chain.root_cause) items.push({ label: 'root', value: chain.root_cause });

  if (items.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Expand causal chain"
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          p: 0,
          m: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
          '&:hover': { textDecoration: 'underline', textDecorationStyle: 'dotted' },
        }}
      >
        {items.map((it) => (
          <Typography
            key={it.label}
            variant="caption"
            component="div"
            sx={{
              fontFamily: 'monospace',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.45,
            }}
            title={`${it.label}: ${it.value}`}
          >
            <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
              {it.label}:
            </Box>
            {it.value}
          </Typography>
        ))}
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="causal-chain-dialog-title"
      >
        <DialogTitle id="causal-chain-dialog-title">Causal chain</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {items.map((it) => (
              <Box key={it.label}>
                <Typography variant="overline" color="text.secondary">
                  {LABELS[it.label] ?? it.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                >
                  {it.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const LABELS: Record<string, string> = {
  sym: 'Symptom',
  prox: 'Proximate cause',
  trig: 'Trigger',
  root: 'Root cause',
};

function ThumbCell({ thumb }: { thumb: ThumbValue | null }): ReactElement {
  if (thumb === 1) {
    return (
      <Tooltip title="Thumb up">
        <ThumbUpAltIcon fontSize="small" color="success" aria-label="Thumb up" />
      </Tooltip>
    );
  }
  if (thumb === -1) {
    return (
      <Tooltip title="Thumb down">
        <ThumbDownAltIcon fontSize="small" color="error" aria-label="Thumb down" />
      </Tooltip>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
}
